'use client'

import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, Package, Truck, MapPin, Clock, FileText, Home } from 'lucide-react'
import { useOrderStore } from '@/stores/order'
import { useCurrencyStore } from '@/stores/currency'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import ECommerceHeader from '@/components/layout/ECommerceHeader'
import Footer from '@/components/layout/Footer'
import GuestAccountPrompt from '@/components/cart/GuestAccountPrompt'

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams()
  const order_number = searchParams.get('order_number')
  const getOrderByNumber = useOrderStore((state) => state.getOrderByNumber)
  const getSymbol = useCurrencyStore((state) => state.getSymbol)
  const convert = useCurrencyStore((state) => state.convert)

  const order = order_number ? getOrderByNumber(order_number) : null

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <ECommerceHeader />
        <main className="container mx-auto px-4 py-16 text-center">
          <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-4">Order Not Found</h1>
          <p className="text-muted-foreground mb-8">We couldn't find the order you're looking for.</p>
          <Link to="/">
            <Button variant="primary">Go to Homepage</Button>
          </Link>
        </main>
        <Footer />
      </div>
    )
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="min-h-screen bg-background">
      <ECommerceHeader />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-success" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Order Confirmed!</h1>
            <p className="text-muted-foreground">Thank you for shopping with BotTola</p>
          </div>

          {/* Guest Account Creation Prompt */}
          <GuestAccountPrompt />

          {/* Order Number */}
          <Card className="p-6 mb-6 text-center">
            <p className="text-sm text-muted-foreground mb-1">Order Number</p>
            <p className="text-2xl font-bold text-foreground">{order.order_number}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Confirmation sent to your email
            </p>
          </Card>

          {/* Order Status Timeline */}
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Order Status</h2>
            <div className="flex items-center justify-between">
              {['Order Placed', 'Processing', 'Shipped', 'Delivered'].map((status, index) => (
                <div key={status} className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      index === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {index === 0 ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : index === 1 ? (
                      <Package className="h-5 w-5" />
                    ) : index === 2 ? (
                      <Truck className="h-5 w-5" />
                    ) : (
                      <Home className="h-5 w-5" />
                    )}
                  </div>
                  <p className="text-xs text-center mt-2">{status}</p>
                  {index < 3 && (
                    <div className="absolute w-full h-1 bg-muted top-5 left-1/2 -z-10" style={{ width: '100%' }} />
                  )}
                </div>
              ))}
            </div>
          </Card>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Delivery Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Delivery Address
              </h2>
              <div className="text-sm space-y-1">
                <p className="font-medium">{order.delivery_address.full_name}</p>
                <p className="text-muted-foreground">{order.delivery_address.address_line1}</p>
                {order.delivery_address.address_line2 && (
                  <p className="text-muted-foreground">{order.delivery_address.address_line2}</p>
                )}
                <p className="text-muted-foreground">
                  {order.delivery_address.area}, {order.delivery_address.city} - {order.delivery_address.postal_code}
                </p>
                <p className="text-muted-foreground">{order.delivery_address.phone}</p>
              </div>
            </Card>

            {/* Delivery Time */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Delivery Time
              </h2>
              <div className="text-sm">
                <p className="font-medium">{formatDate(order.delivery_slot.date)}</p>
                <p className="text-muted-foreground">{order.delivery_slot.label}</p>
              </div>

              <h3 className="text-sm font-medium mt-4 mb-2">Payment Method</h3>
              <p className="text-sm text-muted-foreground capitalize">{order.payment_method.replace('_', ' ')}</p>
            </Card>
          </div>

          {/* Order Items */}
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Order Items ({order.items.length})
            </h2>

            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex gap-4">
                  <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium line-clamp-1">{item.title}</p>
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">
                      {getSymbol()}
                      {convert(item.subtotal).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Price Breakdown */}
            <div className="space-y-2 pt-4 mt-4 border-t border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>
                  {getSymbol()}
                  {convert(order.subtotal).toLocaleString()}
                </span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm text-success">
                  <span>Discount</span>
                  <span>
                    -{getSymbol()}
                    {convert(order.discount).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span>
                  {order.shipping_cost === 0 ? 'Free' : `${getSymbol()}${convert(order.shipping_cost)}`}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <span className="font-semibold">Total</span>
                <span className="text-xl font-bold text-brand-orange">
                  {getSymbol()}
                  {convert(order.total).toLocaleString()}
                </span>
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/" className="flex-1">
              <Button variant="primary" className="w-full">
                Continue Shopping
              </Button>
            </Link>
            <Button variant="outline" className="flex-1">
              <FileText className="h-4 w-4 mr-2" />
              Download Invoice
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-center text-sm text-muted-foreground mt-8">
            Questions about your order?{' '}
            <Link to="/contact" className="text-primary hover:underline">
              Contact Support
            </Link>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  )
}
